import { recolorRive } from 'src/app/shared/utils/rive-recolor.util';

/**
 * A paleta do `document-stack.riv`: as três folhas da animação são os três tipos
 * de documento do RapiDocs, cada uma na cor que o tipo já tem no resto do app
 * (ver `IconColorByDocumentTypePipe`).
 *
 * Os índices abaixo são a ordem dos `SolidColor` dentro do arquivo. Eles saíram
 * de pintar cada um com uma cor distinta e olhar o resultado, e não do nome dos
 * grupos: o arquivo não nomeia paint nenhum. Cada folha usa duas cores, o
 * destaque (as linhas de texto e o bloco de imagem) e a base (o papel).
 *
 * Ficam de fora, de propósito: 3, 7 e 11, a sombra de cada folha, e 12, o
 * retângulo de fundo do artboard, que já vem com alfa zero.
 */
const SHEETS = [
    /** Relatório, a folha da frente quando a animação começa. */
    { token: '--accentBlue', accents: [4, 5], base: 6 },
    /** Contrato. */
    { token: '--accentGreen', accents: [0, 1], base: 2 },
    /** Orçamento. */
    { token: '--accentOrange', accents: [8, 9], base: 10 },
] as const;

/** Cores usadas se o tema ainda não pintou as variáveis (ver `readToken`). */
const FALLBACK: Record<string, number> = {
    '--accentBlue': 0x3381ff,
    '--accentGreen': 0x15be78,
    '--accentOrange': 0xff850a,
};

/**
 * Opacidade do destaque. O arquivo original já desenha linhas e blocos
 * translúcidos sobre o papel, e é isso que dá o ar de "documento visto de
 * longe": em cor cheia as folhas viram bandeiras.
 */
const ACCENT_ALPHA = 0x80;

/** Quanto da cor entra no papel. Acima disso a folha deixa de parecer papel. */
const BASE_TINT = 0.1;

/** Lê `#rrggbb` de uma variável do tema. */
function readToken(token: string): number {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(token).trim();
    const hex = /^#([0-9a-f]{6})$/i.exec(raw);

    return hex ? parseInt(hex[1], 16) : FALLBACK[token];
}

/** Mistura a cor no branco: o papel é branco puxado para o tom do tipo. */
function tint(rgb: number, amount: number): number {
    const mix = (channel: number) => Math.round(255 - amount * (255 - channel));
    const r = mix((rgb >> 16) & 0xff);
    const g = mix((rgb >> 8) & 0xff);
    const b = mix(rgb & 0xff);

    return (0xff << 24) | (r << 16) | (g << 8) | b;
}

/** Devolve o arquivo com as três folhas nas cores dos três tipos de documento. */
export function paintDocumentStack(source: ArrayBuffer): ArrayBuffer {
    const colors = new Map<number, number>();

    for (const sheet of SHEETS) {
        const rgb = readToken(sheet.token);

        for (const index of sheet.accents) colors.set(index, (ACCENT_ALPHA << 24) | rgb);
        colors.set(sheet.base, tint(rgb, BASE_TINT));
    }

    return recolorRive(source, colors);
}
